import { useEffect, useRef, useState } from 'react';
import type { ArchiveRecord, ModerationAction } from '../types/moderation';
import { ApiError, moderateSubmission } from '../lib/api';
import { announce } from '../lib/announce';
import { toast } from '../lib/toast';
import Select from './ui/Select';
import HelpHint from './HelpHint';
import {
  REJECT_REASON_TEMPLATES,
  REQUEST_CHANGES_TEMPLATES,
  type ReasonTemplate,
} from '../lib/reason-templates';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/Dialog';

interface ModerationActionsProps {
  record: ArchiveRecord;
  onModerated: () => void;
  /**
   * When true, the action buttons are disabled. Used when the record
   * is claimed by another moderator (the lock from Phase 3).
   */
  disabled?: boolean;
}

/**
 * Submissions whose latest AI confidence is below this threshold are
 * routed through a two-moderator peer review.
 */
const PEER_REVIEW_CONFIDENCE_THRESHOLD = 0.5;

/**
 * Submissions whose latest AI trace meets all of the following are
 * eligible for the F-001 "quick approve" path that skips the confirm
 * modal:
 *   - decision === auto_approved
 *   - confidence >= QUICK_APPROVE_CONFIDENCE_THRESHOLD
 *   - no safety / PII / duplicate signal firing
 *   - status === pending_review (no peer review parked, no rejected/etc.)
 */
const QUICK_APPROVE_CONFIDENCE_THRESHOLD = 0.85;

/** Compute whether the next approve/reject vote will park for peer review. */
function needsPeerReview(record: ArchiveRecord): boolean {
  if (record.moderationStatus !== 'pending_review') return false;
  if (record.peerReview) return false;
  const last = record.aiAnalysis && record.aiAnalysis[record.aiAnalysis.length - 1];
  if (!last) return false;
  return last.confidence < PEER_REVIEW_CONFIDENCE_THRESHOLD;
}

/**
 * Returns true when the latest AI trace says "auto_approve with high
 * confidence and no warning signals". Used to gate the F-001 one-click
 * approve shortcut so it never bypasses the modal when a human needs
 * to weigh in on PII, duplicate, or safety findings.
 */
export function canQuickApprove(record: ArchiveRecord): boolean {
  if (record.moderationStatus !== 'pending_review') return false;
  if (record.peerReview) return false;
  const last = record.aiAnalysis && record.aiAnalysis[record.aiAnalysis.length - 1];
  if (!last) return false;
  if (last.decision !== 'auto_approved') return false;
  if (last.confidence < QUICK_APPROVE_CONFIDENCE_THRESHOLD) return false;
  const safety = last.signals.safety;
  if (safety.graphicSexual.detected) return false;
  if (safety.graphicViolence.detected) return false;
  if (safety.spam.detected) return false;
  if (safety.needsContext?.detected) return false;
  if (last.signals.pii.detected) return false;
  if (last.signals.duplicate.isDuplicate) return false;
  if (record.sensitive) return false;
  return true;
}

type PendingAction = Exclude<ModerationAction, 'pending_review'> | null;

const ACTION_LABELS: Record<string, string> = {
  approved: 'Approve',
  rejected: 'Reject',
  withdrawn: 'Withdraw',
  awaiting_contributor: 'Request Changes',
};

const ACTION_CONFIRM: Record<string, string> = {
  approved: 'Publish this submission to the public archive.',
  rejected: 'Reject this submission. The contributor will be notified.',
  withdrawn: 'Withdraw this already-published submission.',
  awaiting_contributor:
    'Send the contributor a question and pause moderation until they reply.',
};

const ACTION_PAST_TENSE: Record<string, string> = {
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
  awaiting_contributor: 'sent to the contributor for changes',
};

function templatesFor(action: PendingAction): ReasonTemplate[] {
  if (action === 'rejected') return REJECT_REASON_TEMPLATES;
  if (action === 'awaiting_contributor') return REQUEST_CHANGES_TEMPLATES;
  return [];
}

export default function ModerationActions({
  record,
  onModerated,
  disabled = false,
}: ModerationActionsProps) {
  const [pending, setPending] = useState<PendingAction>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleEdit, setStaleEdit] = useState(false);

  const status = record.moderationStatus;
  const canApprove =
    status === 'pending_review' ||
    status === 'rejected' ||
    status === 'awaiting_contributor' ||
    status === 'awaiting_peer_review';
  const canReject =
    status === 'pending_review' ||
    status === 'approved' ||
    status === 'awaiting_contributor' ||
    status === 'awaiting_peer_review';
  const canWithdraw = status === 'approved';
  const canRequestChanges = status === 'pending_review';
  const peerReviewNeeded = needsPeerReview(record);
  const quickApproveEligible = canQuickApprove(record);

  const open = (action: PendingAction) => {
    setPending(action);
    setNote('');
    setError(null);
  };

  const close = () => {
    if (busy) return;
    setPending(null);
  };

  /**
   * F-001 — Quick approve: skip the modal for clean auto_approved
   * submissions. Used by the Shift+A shortcut and the inline button.
   */
  const quickApprove = async () => {
    if (!quickApproveEligible || disabled || busy) return;
    setBusy(true);
    setError(null);
    setStaleEdit(false);
    try {
      await moderateSubmission(record.submissionId, 'approved', undefined, record.updatedAt);
      toast.success(`${record.title || 'Submission'} approved.`, { announce: false });
      announce(`Submission ${record.title} approved.`);
      onModerated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setStaleEdit(true);
        setError(
          'Someone else updated this submission. Refresh to see the latest before acting.',
        );
        // Surface the error via the modal so the moderator sees it.
        setPending('approved');
      } else {
        setError((err as Error).message || 'Moderation failed');
        setPending('approved');
      }
    } finally {
      setBusy(false);
    }
  };

  // Keyboard shortcuts: a / r / w / c open the relevant modal. Ignored when
  // disabled (claimed by someone else), when a dialog is open, or when the
  // moderator is typing. Enter inside an open confirm dialog triggers confirm.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toUpperCase();
      const editable =
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
      if (editable) return;
      if (pending) {
        // While modal is open, Enter confirms.
        if (e.key === 'Enter' && !busy) {
          e.preventDefault();
          // Will be handled by confirm() below — read via state inside callback.
          confirmRef.current?.();
        }
        return;
      }
      if (disabled) {
        // F-006: surface the claim status to the moderator instead of
        // silently swallowing the keystroke. Only react to known action
        // keys so we don't announce on every keypress.
        if (
          ['a', 'r', 'w', 'c', 'A', 'R', 'W', 'C'].includes(e.key)
        ) {
          announce('This submission is currently claimed by another moderator.');
        }
        return;
      }
      // Shift+A: F-001 quick approve when eligible. The keystroke for
      // capital "A" arrives with shiftKey=true and key="A".
      if (e.shiftKey && (e.key === 'A' || e.key === 'a') && quickApproveEligible && canApprove) {
        e.preventDefault();
        void quickApprove();
        return;
      }
      if (e.shiftKey) return;
      switch (e.key) {
        case 'a':
          if (canApprove) {
            e.preventDefault();
            open('approved');
          }
          break;
        case 'r':
          if (canReject) {
            e.preventDefault();
            open('rejected');
          }
          break;
        case 'w':
          if (canWithdraw) {
            e.preventDefault();
            open('withdrawn');
          }
          break;
        case 'c':
          if (canRequestChanges) {
            e.preventDefault();
            open('awaiting_contributor');
          }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pending, busy, disabled, canApprove, canReject, canWithdraw, canRequestChanges, quickApproveEligible]);

  // confirm() needs a ref so the keyboard handler can call the latest version.
  const confirmRef = useRef<null | (() => void)>(null);

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    setStaleEdit(false);
    try {
      await moderateSubmission(
        record.submissionId,
        pending,
        note || undefined,
        record.updatedAt,
      );
      toast.success(
        `${record.title || 'Submission'} ${ACTION_PAST_TENSE[pending] || pending}.`,
        { announce: false },
      );
      announce(
        `Submission ${record.title} ${ACTION_PAST_TENSE[pending] || pending}.`,
      );
      setPending(null);
      setNote('');
      onModerated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setStaleEdit(true);
        setError(
          'Someone else updated this submission. Refresh to see the latest before acting.',
        );
      } else {
        setError((err as Error).message || 'Moderation failed');
      }
    } finally {
      setBusy(false);
    }
  };
  confirmRef.current = confirm;

  const templates = templatesFor(pending);

  return (
    <aside className="moderation-actions" aria-label="Moderation actions">
      <h3>Actions</h3>
      {canApprove && quickApproveEligible && (
        <div className="moderation-actions__row moderation-actions__row--quick">
          <button
            type="button"
            className="btn btn-primary moderation-actions__approve moderation-actions__quick-approve"
            onClick={() => void quickApprove()}
            disabled={disabled || busy}
            data-testid="action-quick-approve"
            aria-keyshortcuts="Shift+A"
            aria-describedby={disabled ? 'claim-banner-id' : undefined}
          >
            Quick approve
            <kbd className="action-kbd" aria-hidden="true">⇧A</kbd>
          </button>
          <HelpHint topic="Quick approve">
            <p>
              The AI flagged this submission as auto-approve with high
              confidence and no PII, duplicate, or safety signals. Skip
              the confirm step. Press Shift+A as a shortcut.
            </p>
            <a href="#/guide#decision-standards">Read more →</a>
          </HelpHint>
        </div>
      )}
      {canApprove && (
        <div className="moderation-actions__row">
          <button
            type="button"
            className={`btn btn-primary moderation-actions__approve${quickApproveEligible ? ' moderation-actions__approve--secondary' : ''}`}
            onClick={() => open('approved')}
            disabled={disabled}
            data-testid="action-approve"
            aria-keyshortcuts="a"
            aria-describedby={disabled ? 'claim-banner-id' : undefined}
          >
            {peerReviewNeeded ? 'Approve (peer review needed)' : quickApproveEligible ? 'Approve with note' : 'Approve'}
            <kbd className="action-kbd" aria-hidden="true">a</kbd>
          </button>
          <HelpHint topic="Approve">
            <p>
              Publish the submission. Use when scope, rights, metadata, and
              safety look right; flag sensitive content separately rather
              than rejecting.
            </p>
            <a href="#/guide#decision-standards">Read more →</a>
          </HelpHint>
        </div>
      )}
      {canReject && (
        <div className="moderation-actions__row">
          <button
            type="button"
            className="btn btn-secondary moderation-actions__reject"
            onClick={() => open('rejected')}
            disabled={disabled}
            data-testid="action-reject"
            aria-keyshortcuts="r"
            aria-describedby={disabled ? 'claim-banner-id' : undefined}
          >
            {peerReviewNeeded ? 'Reject (peer review needed)' : 'Reject'}
            <kbd className="action-kbd" aria-hidden="true">r</kbd>
          </button>
          <HelpHint topic="Reject">
            <p>
              Decline the submission. Use for out-of-scope, rights issues,
              unsafe content, or files that cannot be reviewed. The
              contributor is emailed your reason.
            </p>
            <a href="#/guide#decision-standards">Read more →</a>
          </HelpHint>
        </div>
      )}
      {canRequestChanges && (
        <div className="moderation-actions__row">
          <button
            type="button"
            className="btn btn-secondary moderation-actions__request-changes"
            onClick={() => open('awaiting_contributor')}
            disabled={disabled}
            data-testid="action-request-changes"
            aria-keyshortcuts="c"
            aria-describedby={disabled ? 'claim-banner-id' : undefined}
          >
            Request Changes
            <kbd className="action-kbd" aria-hidden="true">c</kbd>
          </button>
          <HelpHint topic="Request Changes">
            <p>
              Ask the contributor a clarifying question and park the record
              as awaiting_contributor until they reply. Use when the item is
              90 percent there but missing one piece of context.
            </p>
            <a href="#/guide#decision-standards">Read more →</a>
          </HelpHint>
        </div>
      )}
      {canWithdraw && (
        <div className="moderation-actions__row moderation-actions__row--withdraw">
          <button
            type="button"
            className="btn btn-secondary moderation-actions__withdraw"
            onClick={() => open('withdrawn')}
            disabled={disabled}
            data-testid="action-withdraw"
            aria-keyshortcuts="w"
            aria-describedby={disabled ? 'claim-banner-id' : undefined}
          >
            Withdraw
            <kbd className="action-kbd" aria-hidden="true">w</kbd>
          </button>
          <HelpHint topic="Withdraw">
            <p>
              Unpublish an already-approved item. Use for contributor
              removal requests or when newly discovered concerns make
              continued publication unsafe.
            </p>
            <a href="#/guide#decision-standards">Read more →</a>
          </HelpHint>
        </div>
      )}

      <Dialog open={pending !== null} onOpenChange={(o) => !o && close()}>
        {pending && (
          <DialogContent>
            <DialogTitle>{ACTION_LABELS[pending]}</DialogTitle>
            <DialogDescription>{ACTION_CONFIRM[pending]}</DialogDescription>
            {templates.length > 0 && (
              <label className="form-group">
                <span>Templates</span>
                <Select
                  options={['Custom...', ...templates.map((t) => t.label)]}
                  value="Custom..."
                  onValueChange={(v) => {
                    const tpl = templates.find((t) => t.label === v);
                    if (tpl) setNote(tpl.body);
                  }}
                />
              </label>
            )}
            <label className="form-group">
              <span>
                {pending === 'awaiting_contributor'
                  ? 'Question for the contributor'
                  : 'Note (optional)'}
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="moderation-note"
                placeholder={
                  pending === 'awaiting_contributor'
                    ? 'What do you need from the contributor?'
                    : 'Add context for the audit trail'
                }
                rows={pending === 'awaiting_contributor' || pending === 'rejected' ? 6 : 3}
              />
            </label>
            {error && <div className="field-error" role="alert">{error}</div>}
            <div className="dlg-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={close}
                disabled={busy}
              >
                Cancel
              </button>
              {staleEdit && (
                // F-104: explicitly tells the user what Refresh does so
                // they don't lose their typed note unintentionally. The
                // note stays put if they Cancel instead.
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    onModerated();
                    setPending(null);
                  }}
                  data-testid="moderation-refresh"
                  title="Reload the submission. Your typed note will be lost."
                >
                  Refresh (loses your note)
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirm}
                disabled={
                  busy ||
                  (pending === 'awaiting_contributor' && !note.trim())
                }
                data-testid="moderation-confirm"
              >
                {busy ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </aside>
  );
}
